import { Fragment, useEffect } from 'react';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { repositionCookieBanner } from '@Components/Utilities/SandhillsUtility';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import ComplexBreadcrumb from '@Components/Shared/ComplexBreadcrumb';
import TranslationText from '@Components/Utilities/TranslationText';
import MediaControl from '@Components/Shared/MediaControl';
import CategoryInformation from '@Components/Shared/CategoryInformation';
import ready from 'document-ready';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import translator from '@Components/Utilities/Translator';

const LoanApprovalEstimateDisclaimer = loadable(() => import('@Components/Shared/LoanApprovalEstimate/LoanApprovalEstimateDisclaimer'));
const LoanApprovalEstimateBanner = loadable(() => import('@Components/Shared/LoanApprovalEstimate/LoanApprovalEstimateBanner'));
const LoanApprovalEstimateModal = loadable(() => import('@Components/Shared/Modals/LoanApprovalEstimateModal'), true);

const StolenEquipmentDetailView = (props) => {

    const windowSize = useWindowSize();
    const imageCarouselClass = 'media-container ' + (props.MediaModel.ShowThumbnails && props.MediaModel.Media.length > 0 ? '' : 'media-container_empty');
    const currentSite = useRecoilValue(atoms.currentWebsite);

    useEffect(() => {
        const cookieBannerResize = () => {
            repositionCookieBanner('detail-contact-bar', 1001);
        };

        ready(() => {
            cookieBannerResize();
            window.addEventListener('resize', cookieBannerResize);
        });

        return () => {
            window.removeEventListener('resize', cookieBannerResize);
        };
    }, []);

    return (
        <div className='detail-wrapper'>
            <CategoryInformation {...props.CategoryInformation} />

            <div className='detail__breadcrumbs'>
                <ComplexBreadcrumb BreadCrumbModels={props.BreadCrumbs} />
                {!currentSite.IsMobileAppContext &&
                    <Button variant='text' size='small' className='detail__search-results' onClick={() => history.go(-1)}>
                        <Icon baseClassName='fas' className='fa-angle-double-left' />
                        <span className='detail__search-results-text'><TranslationText tKey='sandhills.mobileapps.searchresults' /></span>
                    </Button>
                }
            </div>

            {windowSize.isMobile &&
                <div className='detail__heading'>
                    <div className='detail__title-container'>
                        <h1 className='detail__title'>{props.StolenEquipmentTitle}</h1>
                    </div>
                    <div className='detail__category-container'>
                        <h2 className='detail__category'>{props.DisplayCategoryName}</h2>
                    </div>
                </div>
            }

            <div className='detail__banner-ad'>
                {props.LoanApprovalEstimateModel.ShowBanner &&
                    <LoanApprovalEstimateBanner {...props.LoanApprovalEstimateModel} pageType='detail' />}
            </div>

            <main id='main-content' className='detail__main-content'>
                <div className='detail__left-col'>
                    <div className={imageCarouselClass}>
                        {props.MediaModel.Media && props.MediaModel.Media.length > 0 ?
                            <MediaControl {...props.MediaModel} /> :
                            <div className='media-control-no-photo' />}
                    </div>
                </div>

                <div className='detail__right-col'>
                    <div className='detail__details'>
                        {!windowSize.isMobile &&
                            <div className='detail__heading'>
                                <div className='detail__title-container'>
                                    <h1 className='detail__title'>{props.StolenEquipmentTitle}</h1>
                                </div>
                                <div className='detail__category-container'>
                                    <h2 className='detail__category'>{props.DisplayCategoryName}</h2>
                                </div>
                            </div>
                        }

                        {(props.StolenLocation?.FormattedLocation && props.StolenLocation.GoogleMapsUrl) &&
                            <div className='detail__machine-location'>
                                <span className='detail__machine-location-label-text'>{props.StolenLocation.DisplayText}</span>
                                <a href={props.StolenLocation.GoogleMapsUrl} className={props.StolenLocation.GoogleMapsUrl ? 'detail__machine-location-link' : ''} target='_blank' aria-label={props.StolenLocation.FormattedLocation + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                    {props.StolenLocation.FormattedLocation}
                                </a>
                                {props.StolenLocation.FormattedLocation &&
                                    <i className='fas fa-external-link-alt' aria-hidden='true' />}
                            </div>}

                        {props.ShowDealer &&
                            <div className='detail__contact-info'>
                                <div className='dealer-contact'>
                                    <div className='dealer-contact__header'>
                                        <h3 className='dealer-contact-heading'>{props.DealerName}</h3>
                                    </div>

                                    <div className='dealer-contact__details'>
                                        {props.DealerContactName &&
                                            <div className='dealer-contact__name'>
                                                <strong className='dealer-contact__label'><TranslationText tKey='sandhills.tradepubwebsite.mvc.contact' />:</strong>
                                                {props.DealerContactName}
                                            </div>}

                                        <div className='dealer-contact__phone'>
                                            <span className='visually-hidden'><TranslationText tKey='sandhills.phone' />:</span>
                                            <a id='stolen-detail-dealer-phone' href={'tel:' + props.DealerPhoneNumberUnformatted}>
                                                {props.DealerPhoneNumberFormatted}
                                            </a>
                                        </div>
                                        <div className='dealer-contact__location'>{props.DealerLocation}</div>
                                    </div>
                                </div>
                            </div>}
                    </div>
                </div>

                <div className='detail__specs'>
                    {(props?.Details?.Specs && Object.keys(props.Details.Specs)?.length > 0) &&
                        <>
                            <h3 className='detail__specs-heading'>{props.Details.GroupName}</h3>
                            <div className='detail__specs-wrapper'>
                                {Object.keys(props.Details.Specs).map((key, index) =>
                                    <Fragment key={key}>
                                        {props.Details.Specs[key] &&
                                            <>
                                                <div className='detail__specs-label'>
                                                    {key}
                                                </div>
                                                <div className='detail__specs-value'>
                                                    {props.Details.Specs[key]}
                                                </div>
                                            </>}
                                    </Fragment>
                                )}
                            </div>
                        </>}

                    {(props?.General?.Specs && Object.keys(props.General.Specs)?.length > 0) &&
                        <>
                            <h3 className='detail__specs-heading'>{props.General.GroupName}</h3>
                            <div className='detail__specs-wrapper'>
                                {Object.keys(props.General.Specs).map((key, index) =>
                                    <Fragment key={key}>
                                        {props.General.Specs[key] &&
                                            <>
                                                <div className='detail__specs-label'>
                                                    {key}
                                                </div>
                                                <div className='detail__specs-value'>
                                                    {props.General.Specs[key]}
                                                </div>
                                            </>}
                                    </Fragment>
                                )}
                            </div>
                        </>}

                    {(props?.LawEnforcementAgency?.Specs && Object.keys(props.LawEnforcementAgency.Specs)?.length > 0) &&
                        <>
                            <h3 className='detail__specs-heading'>{props.LawEnforcementAgency.GroupName}</h3>
                            <div className='detail__specs-wrapper'>
                                {Object.keys(props.LawEnforcementAgency.Specs).map((key, index) =>
                                    <Fragment key={key}>
                                        {props.LawEnforcementAgency.Specs[key] &&
                                            <>
                                                <div className='detail__specs-label'>
                                                    {key}
                                                </div>
                                                <div className='detail__specs-value'>
                                                    {props.LawEnforcementAgency.Specs[key]}
                                                </div>
                                            </>}
                                    </Fragment>
                                )}
                            </div>
                        </>}
                </div>

                <div className='detail__legal-notices'>
                    {props.LoanApprovalEstimateModel && props.LoanApprovalEstimateModel.ShowDisclaimer && props.LoanApprovalEstimateModel.ShowBanner &&
                        <LoanApprovalEstimateDisclaimer Disclaimer={props.LoanApprovalEstimateModel.Disclaimer} />}
                </div>
            </main>

            {props.LoanApprovalEstimateModel && props.LoanApprovalEstimateModel.ShowBanner &&
                <LoanApprovalEstimateModal {...props.LoanApprovalEstimateModel} />}
        </div>
    );
};

export default StolenEquipmentDetailView;
global.StolenEquipmentDetailView = StolenEquipmentDetailView;